import noop from 'lodash/noop';
import get from 'lodash/get';
import tuner from './tuner';
import webApiClientSingleton from './webApiClientSingleton';
import Timer from './utils/Timer';

const defaultTtl = 15 * 1000;
const timer = new Timer();
let guideId;
let itemToken;
let token;
let onNowPlayingChangedHandler;
let previousNowPlaying;
let fetchNowPlaying;
let useBoostMetadata;
let boostTransitionTitle;

// Exported for testing
export function fetchAndRepeat(shouldRepeat) {
  return fetchNowPlaying().then((params) => {
    if (shouldRepeat) {
      setupNextRequest(shouldRepeat, get(params, 'ttl')); // eslint-disable-line no-use-before-define
    }
    return params;
  });
}

function setupNextRequest(shouldRepeat, ttl) {
  timer.start(() => fetchAndRepeat(shouldRepeat), ttl || defaultTtl);
}

function maybeApplyBoostMetadata(nowPlaying) {
  if (!nowPlaying.boost) {
    return nowPlaying;
  }

  const { primary = {}, boost = {} } = nowPlaying || {};
  const { title, subtitle, image } = useBoostMetadata ? boost : primary;
  const { subtitle: otherSubtitle } = !useBoostMetadata ? boost : primary;

  return {
    ...nowPlaying,
    title: boostTransitionTitle || title,
    subtitle: boostTransitionTitle ? otherSubtitle : subtitle,
    image,
  };
}

function onNowPlayingFetched(response) {
  const { nowPlaying: responseNowPlaying, token: nowPlayingToken } = response;
  const nowPlaying = maybeApplyBoostMetadata(responseNowPlaying);

  token = nowPlayingToken;

  // We need the boost guide id for reporting, but the Now Playing API is the only one that provides
  // it as of now, so we'll rely on this hacky-feeling logic for now. In the future, if Boost API's
  // change or the boost guide id is added to the Tune API payload, we can update this.
  if (nowPlaying.boost && nowPlaying.boost.guideId) {
    tuner.boostState.guideId = nowPlaying.boost.guideId;
  }

  if (!previousNowPlaying || previousNowPlaying.title !== nowPlaying.title) {
    previousNowPlaying = nowPlaying;
    onNowPlayingChangedHandler(nowPlaying);
  }

  return nowPlaying;
}

fetchNowPlaying = function () {
  if (!guideId) {
    return Promise.reject();
  }

  return webApiClientSingleton.instance.profile
    .nowPlaying(guideId, { token, itemToken })
    .then(onNowPlayingFetched)
    .catch(noop);
};

function poll(id, handler, shouldRepeat) {
  onNowPlayingChangedHandler = handler;

  guideId = id;
  itemToken = tuner.tune.itemToken;
  return fetchAndRepeat(shouldRepeat);
}

function stop() {
  timer.clear();
  previousNowPlaying = null;
  guideId = null;
}

export default {
  start(id, handler) {
    return poll(id, handler, true);
  },
  stop() {
    stop();
  },
  fetchCurrentNowPlaying(id) {
    return this.fetchOnce(id, noop);
  },
  fetchOnce(id, handler) {
    return poll(id, handler, false);
  },
  toggleBoostMetadata(trueOrFalse) {
    useBoostMetadata = trueOrFalse;
    boostTransitionTitle = null;

    if (previousNowPlaying) {
      const nextNowPlaying = maybeApplyBoostMetadata(previousNowPlaying);
      onNowPlayingChangedHandler(nextNowPlaying);
    }
  },
  showBoostTransitionTitle(title) {
    boostTransitionTitle = title;

    if (previousNowPlaying) {
      const nextNowPlaying = maybeApplyBoostMetadata(previousNowPlaying);
      onNowPlayingChangedHandler(nextNowPlaying);
    }
  },
  setRejectReasonKey(rejectReasonKey) {
    previousNowPlaying.rejectReasonKey = rejectReasonKey;
    onNowPlayingChangedHandler(previousNowPlaying);
    stop();
  },
};
